import { DEV_DEFAULTS } from '../config/devDefaults';
import type { WaiterSession } from './session.types';

/*
 * Token & session store (same shape as crs-mobile-app, waiter keys).
 *
 *   Access token     → in-memory + sessionStorage `wtr.access` (F5-safe)
 *   Session          → sessionStorage `wtr.session`
 *   Refresh token    → localStorage `wtr.refresh`
 *   Tenant / App ID  → localStorage `wtr.tenantId` / `wtr.appId`, falling
 *                      back to VITE_DEFAULT_TENANT_ID / VITE_APP_ID pre-login
 */

const ACCESS_KEY = 'wtr.access';
const SESSION_KEY = 'wtr.session';
const REFRESH_KEY = 'wtr.refresh';
const TENANT_KEY = 'wtr.tenantId';
const APP_KEY = 'wtr.appId';
/** Multi-outlet: the outlet chosen at sign-in, sent as X-Outlet-ID. Per tab, like the session. */
const OUTLET_KEY = 'wtr.outletId';
/** The section the waiter works in (spec 2.41 §12.1a); chosen after the outlet, cleared with it. */
const SECTION_KEY = 'wtr.sectionId';
const SECTION_NAME_KEY = 'wtr.sectionName';
const SECTION_CHOSEN_KEY = 'wtr.sectionChosen';

let accessToken: string | null = null;

function readLs(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLs(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Storage unavailable (private mode etc.) — swallow.
  }
}

function readSs(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSs(key: string, value: string | null): void {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export const tokenStore = {
  // ---- Access token ----------------------------------------------------
  getAccessToken(): string | null {
    if (accessToken) return accessToken;
    const persisted = readSs(ACCESS_KEY);
    if (persisted) accessToken = persisted;
    return accessToken;
  },
  setAccessToken(token: string | null): void {
    accessToken = token;
    writeSs(ACCESS_KEY, token);
  },

  // ---- Refresh token ---------------------------------------------------
  getRefreshToken(): string | null {
    return readLs(REFRESH_KEY);
  },
  setRefreshToken(token: string | null): void {
    writeLs(REFRESH_KEY, token);
  },

  // ---- Session (F5-restore) --------------------------------------------
  getPersistedSession(): WaiterSession | null {
    const raw = readSs(SESSION_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as WaiterSession;
    } catch {
      return null;
    }
  },
  setPersistedSession(session: WaiterSession | null): void {
    writeSs(SESSION_KEY, session ? JSON.stringify(session) : null);
  },

  // ---- Tenant / app context --------------------------------------------
  getTenantId(): string | null {
    return readLs(TENANT_KEY) ?? DEV_DEFAULTS.tenantId;
  },
  setTenantId(id: string | null): void {
    writeLs(TENANT_KEY, id);
  },

  getAppId(): string | null {
    return readLs(APP_KEY) ?? DEV_DEFAULTS.appId;
  },
  setAppId(id: string | null): void {
    writeLs(APP_KEY, id);
  },

  // ---- Reset -----------------------------------------------------------
  /** Clears auth artifacts. Tenant / app context survive (user preferences). */
  getOutletId(): string | null {
    return readSs(OUTLET_KEY);
  },
  setOutletId(id: string | null): void {
    writeSs(OUTLET_KEY, id);
  },

  getSectionId(): string | null {
    return readSs(SECTION_KEY);
  },
  getSectionName(): string | null {
    return readSs(SECTION_NAME_KEY);
  },
  /** True once the section step is done: a section, or 'none' for an outlet without sections. */
  isSectionChosen(): boolean {
    return readSs(SECTION_CHOSEN_KEY) === '1';
  },
  /** A section, 'none' (the outlet has no sections: whole floor), or null to ask again. */
  setSection(section: { id: string; name: string } | 'none' | null): void {
    const s = section && section !== 'none' ? section : null;
    writeSs(SECTION_KEY, s?.id ?? null);
    writeSs(SECTION_NAME_KEY, s?.name ?? null);
    writeSs(SECTION_CHOSEN_KEY, section ? '1' : null);
  },

  clear(): void {
    accessToken = null;
    writeSs(ACCESS_KEY, null);
    writeSs(SESSION_KEY, null);
    writeSs(OUTLET_KEY, null);
    writeSs(SECTION_KEY, null);
    writeSs(SECTION_NAME_KEY, null);
    writeSs(SECTION_CHOSEN_KEY, null);
    writeLs(REFRESH_KEY, null);
  },
};
